import { Component, ComponentType } from "@/types";
import { chartTypeRegistry } from "./ChartTypeRegistry";
import { chartDataManager } from "./ChartDataManager";
import { ChartPropertyChange, commonChartHandler } from "./handlers/CommonChartHandler";
import { lineChartHandler } from "./handlers/LineChartHandler";
import { barChartHandler } from "./handlers/BarChartHandler";
import { pieChartHandler } from "./handlers/PieChartHandler";
import { areaChartHandler } from "./handlers/AreaChartHandler";
import { scatterChartHandler } from "./handlers/ScatterChartHandler";
import { chartTemplateManager } from "./templates/ChartTemplates";

export interface ChartCommand {
  type: "property" | "data" | "style" | "conversion" | "template";
  changes?: ChartPropertyChange;
  confidence: number;
  suggestions?: string[];
  description?: string;
}

export interface ChartParserResult {
  success: boolean;
  command?: ChartCommand;
  error?: string;
  suggestions?: string[];
}

/**
 * Main chart command parser that routes to specific handlers
 */
export class ChartCommandParser {
  private static instance: ChartCommandParser;

  static getInstance(): ChartCommandParser {
    if (!ChartCommandParser.instance) {
      ChartCommandParser.instance = new ChartCommandParser();
    }
    return ChartCommandParser.instance;
  }

  /**
   * Parse a natural language command for a chart component
   */
  parseCommand(command: string, component: Component): ChartParserResult {
    // Validate it's a chart component
    if (!chartTypeRegistry.isChartType(component.type)) {
      return {
        success: false,
        error: "Component is not a chart type",
      };
    }

    try {
      // Check for template commands first
      const template = chartTemplateManager.findTemplateByCommand(command);
      if (template) {
        const templateConfig = chartTemplateManager.applyTemplate(template);
        const changes: ChartPropertyChange = {
          type: templateConfig.type,
          properties: templateConfig.properties,
          data: templateConfig.data,
        };

        return {
          success: true,
          command: {
            type: "template",
            changes,
            confidence: 0.9,
            description: `Applied template: ${template.name}`,
          },
          suggestions: this.getSuggestions(component),
        };
      }

      // Route to appropriate handler based on chart type
      let changes: ChartPropertyChange | null = null;

      switch (component.type) {
        case ComponentType.LINE_CHART:
          changes = lineChartHandler.parseCommand(command, component);
          break;

        case ComponentType.BAR_CHART:
          changes = barChartHandler.parseCommand(command, component);
          break;

        case ComponentType.PIE_CHART:
          changes = pieChartHandler.parseCommand(command, component);
          break;

        case ComponentType.AREA_CHART:
          changes = areaChartHandler.parseCommand(command, component);
          break;

        case ComponentType.SCATTER_CHART:
          changes = scatterChartHandler.parseCommand(command, component);
          break;

        default:
          changes = commonChartHandler.parseCommand(command, component);
      }

      if (changes) {
        // Validate changes
        const validation = this.validateChanges(changes, component);
        if (!validation.valid) {
          return {
            success: false,
            error: validation.error,
            suggestions: this.getSuggestions(component),
          };
        }

        // Determine command type
        const commandType = this.determineCommandType(changes);

        return {
          success: true,
          command: {
            type: commandType,
            changes,
            confidence: this.calculateConfidence(command, changes),
            description: this.generateDescription(changes, component),
          },
          suggestions: this.getSuggestions(component),
        };
      }

      // No changes parsed - provide suggestions
      return {
        success: false,
        error: "Could not understand the command",
        suggestions: this.getSuggestions(component),
      };

    } catch (error) {
      console.error("Error parsing chart command:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to parse command",
        suggestions: this.getSuggestions(component),
      };
    }
  }

  /**
   * Parse multiple commands in sequence
   */
  parseMultipleCommands(commands: string[], component: Component): ChartParserResult[] {
    const results: ChartParserResult[] = [];
    let currentComponent = { ...component };

    for (const command of commands) {
      const result = this.parseCommand(command, currentComponent);
      results.push(result);

      // Apply changes to component for next command
      if (result.success && result.command?.changes) {
        currentComponent = this.applyChangesToComponent(currentComponent, result.command.changes);
      }
    }

    return results;
  }

  /**
   * Validate that changes are appropriate for the component
   */
  private validateChanges(changes: ChartPropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate data if present (skip for type conversions — data is validated against old type which is wrong)
    if (changes.data && !changes.type) {
      const validation = chartDataManager.validateDataForChartType(changes.data, component.type);
      if (!validation.valid) {
        return {
          valid: false,
          error: `Data validation failed: ${validation.errors.join(", ")}`,
        };
      }
    }

    // Validate properties against schema (skip for type conversions — properties target the new type)
    if (changes.properties && !changes.type) {
      for (const [key, value] of Object.entries(changes.properties)) {
        if (!chartTypeRegistry.validateProperty(component.type, key, value)) {
          return {
            valid: false,
            error: `Invalid value for property "${key}"`,
          };
        }
      }
    }

    // Validate chart type conversion
    if (changes.type) {
      if (!chartTypeRegistry.canConvertTo(component.type, changes.type)) {
        return {
          valid: false,
          error: `Cannot convert from ${component.type} to ${changes.type}`,
        };
      }
    }

    return { valid: true };
  }

  /**
   * Determine the type of command based on changes
   */
  private determineCommandType(changes: ChartPropertyChange): ChartCommand["type"] {
    if (changes.type) return "conversion";
    if (changes.data) return "data";
    if (changes.style) return "style";
    return "property";
  }

  /**
   * Calculate confidence score for the parsed command
   */
  private calculateConfidence(command: string, changes: ChartPropertyChange): number {
    let confidence = 0.5; // Base confidence

    // Increase confidence for specific patterns
    if (command.includes("set") || command.includes("change") || command.includes("make")) {
      confidence += 0.1;
    }

    // Increase for quoted values
    if (command.includes('"') || command.includes("'")) {
      confidence += 0.15;
    }

    // Increase for numeric values
    if (/\d+/.test(command)) {
      confidence += 0.1;
    }

    // Increase based on number of changes
    const changeCount = Object.keys(changes.properties || {}).length +
                       Object.keys(changes.style || {}).length +
                       (changes.data ? 1 : 0) +
                       (changes.type ? 1 : 0);

    if (changeCount > 0) {
      confidence += Math.min(changeCount * 0.05, 0.25);
    }

    return Math.min(confidence, 1.0);
  }

  /**
   * Generate a description of the changes
   */
  private generateDescription(changes: ChartPropertyChange, component: Component): string {
    const descriptions: string[] = [];

    if (changes.type) {
      const newTypeConfig = chartTypeRegistry.getConfig(changes.type);
      descriptions.push(`Convert to ${newTypeConfig?.name || changes.type}`);
    }

    if (changes.data) {
      descriptions.push(`Update chart data (${changes.data.length} points)`);
    }

    if (changes.properties) {
      const propDescriptions: string[] = [];

      if (changes.properties.title) {
        propDescriptions.push(`title: "${changes.properties.title}"`);
      }
      if (changes.properties.colors) {
        propDescriptions.push(`colors updated`);
      }
      if (changes.properties.strokeWidth !== undefined) {
        propDescriptions.push(`line width: ${changes.properties.strokeWidth}px`);
      }
      if (changes.properties.showDots !== undefined) {
        propDescriptions.push(`dots: ${changes.properties.showDots ? "shown" : "hidden"}`);
      }
      if (changes.properties.strokeDasharray) {
        propDescriptions.push(`line style: dashed`);
      }

      if (propDescriptions.length > 0) {
        descriptions.push(`Set ${propDescriptions.join(", ")}`);
      }
    }

    return descriptions.join("; ") || "Update chart";
  }

  /**
   * Get suggestions based on component state
   */
  private getSuggestions(component: Component): string[] {
    switch (component.type) {
      case ComponentType.LINE_CHART:
        return lineChartHandler.getSuggestions(component);
      case ComponentType.BAR_CHART:
        return barChartHandler.getSuggestions(component);
      case ComponentType.PIE_CHART:
        return pieChartHandler.getSuggestions(component);
      case ComponentType.AREA_CHART:
        return areaChartHandler.getSuggestions(component);
      case ComponentType.SCATTER_CHART:
        return scatterChartHandler.getSuggestions(component);
      default:
        return commonChartHandler.getSuggestions(component);
    }
  }

  /**
   * Apply changes to a component (for preview/simulation)
   */
  private applyChangesToComponent(component: Component, changes: ChartPropertyChange): Component {
    const updated = { ...component };

    if (changes.type) {
      updated.type = changes.type;
    }

    if (changes.properties) {
      updated.properties = {
        ...updated.properties,
        ...changes.properties,
      };
    }

    if (changes.style) {
      updated.style = {
        ...updated.style,
        ...changes.style,
      };
    }

    if (changes.data) {
      updated.properties = {
        ...updated.properties,
        data: changes.data,
      };
    }

    return updated;
  }

  /**
   * Generate example commands for a chart type
   */
  getExampleCommands(chartType: ComponentType): string[] {
    const config = chartTypeRegistry.getConfig(chartType);
    const examples = config?.aiCommands || [];

    // Add common examples
    examples.push(
      'Set title to "Monthly Sales"',
      "Add data point March with 500",
      "Use blue color scheme",
      "Show legend",
      "Hide grid lines",
      "Generate sample data"
    );

    // Add type-specific examples
    if (chartType === ComponentType.LINE_CHART) {
      examples.push(
        "Make the line thicker",
        "Use dashed line",
        "Show data points",
        "Smooth the curve",
        "Add moving average",
        "Fill area under line"
      );
    } else if (chartType === ComponentType.PIE_CHART) {
      examples.push(
        "Make it a donut chart",
        "Show percentage labels",
        "Use rainbow colors"
      );
    } else if (chartType === ComponentType.BAR_CHART) {
      examples.push(
        "Stack the bars",
        "Make horizontal bars",
        "Increase bar spacing"
      );
    }

    return examples;
  }

  /**
   * Check if a command is likely a chart command
   */
  isChartCommand(command: string): boolean {
    const chartKeywords = [
      // Data operations
      "data", "point", "value", "add", "remove", "update", "clear",
      // Chart elements
      "title", "legend", "grid", "axis", "label", "tooltip",
      // Styling
      "color", "theme", "style", "width", "thickness",
      // Chart-specific
      "line", "bar", "pie", "area", "scatter", "chart",
      // Line-specific
      "dot", "marker", "smooth", "curve", "dashed", "solid",
      // Actions
      "convert", "transform", "generate", "import"
    ];

    const lowerCommand = command.toLowerCase();
    return chartKeywords.some(keyword => lowerCommand.includes(keyword));
  }
}

export const chartCommandParser = ChartCommandParser.getInstance();
